import clsx from "clsx";
import { Binoculars, ChevronDown, RefreshCw, Trash2 } from "lucide-react";
import { useEffect, useState, type MouseEvent, type ReactNode } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import type { Job, Watch } from "@/lib/types";

import { useCancelWatch, useWatch, useWatches } from "@/hooks/useWatches";
import { useStore } from "@/lib/store";

import { StatusBadge } from "../StatusBadge";
import {
  describeNotifyTarget,
  describeTriggerResult,
  describeWatchCondition,
  formatAbsoluteTime,
  getWatchStatusTone,
  getWatchTitle,
  relativeTime,
} from "./utils";

export function WatchesView(): JSX.Element {
  const [scope, setScope] = useState<"active" | "all">("active");
  const [ownerScope, setOwnerScope] = useState<"personal" | "all">("personal");
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const isAdmin = useStore((state) => state.user?.role === "admin");
  const { data, isLoading, isFetching, refetch } = useWatches(scope, ownerScope);
  const watches = data?.watches ?? [];

  useEffect(() => {
    if (expandedId && !watches.some((watch) => watch.id === expandedId)) setExpandedId(null);
  }, [expandedId, watches]);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex-shrink-0 border-b border-cyber-border px-4 py-3">
        <div className="flex items-center justify-between gap-3 px-6">
          <div className="flex items-center gap-2">
            <Binoculars size={16} className="text-cyber-cyan" />
            <h2 className="text-sm font-medium text-cyber-text">Watches</h2>
            <span className="rounded-full bg-cyber-surface px-1.5 py-0.5 text-[10px] text-cyber-muted">
              {watches.length} {scope === "active" ? "active" : "total"}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {isAdmin && (
              <Toggle
                values={["personal", "all"]}
                selected={ownerScope}
                onSelect={(value) => {
                  setOwnerScope(value);
                  setExpandedId(null);
                }}
              />
            )}
            <Toggle
              values={["active", "all"]}
              selected={scope}
              onSelect={(value) => {
                setScope(value);
                setExpandedId(null);
              }}
            />
            <button
              onClick={() => refetch()}
              className="rounded p-1.5 text-cyber-muted transition-colors hover:bg-cyber-surface/50 hover:text-cyber-cyan"
              title="Refresh watches"
            >
              <RefreshCw size={14} className={isFetching ? "animate-spin" : ""} />
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        <div className="mx-auto max-w-4xl space-y-2">
          {isLoading ? (
            <div className="py-8 text-center text-cyber-muted">Loading watches...</div>
          ) : watches.length === 0 ? (
            <div className="py-16 text-center">
              <Binoculars size={32} className="mx-auto mb-3 text-cyber-muted/30" />
              <p className="mb-1 text-sm text-cyber-muted">
                No {scope === "active" ? "active " : ""}watches
              </p>
              <p className="text-xs text-cyber-muted/60">
                Ask Mako to watch an event and it will show up here.
              </p>
            </div>
          ) : (
            watches.map((watch) => (
              <WatchCard
                key={watch.id}
                watch={watch}
                showOwner={ownerScope === "all"}
                expanded={expandedId === watch.id}
                onToggle={() => setExpandedId(expandedId === watch.id ? null : watch.id)}
                onCancelled={() => scope === "active" && setExpandedId(null)}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
}

function Toggle<T extends string>({
  values,
  selected,
  onSelect,
}: {
  values: readonly T[];
  selected: T;
  onSelect: (value: T) => void;
}) {
  return (
    <div className="flex items-center overflow-hidden rounded border border-cyber-border">
      {values.map((value) => (
        <button
          key={value}
          onClick={() => onSelect(value)}
          className={clsx(
            "px-2 py-1 text-xs capitalize transition-colors",
            selected === value
              ? "bg-cyber-cyan/10 text-cyber-cyan"
              : "text-cyber-muted hover:bg-cyber-surface/50 hover:text-cyber-text",
          )}
        >
          {value}
        </button>
      ))}
    </div>
  );
}

function WatchCard({
  watch,
  showOwner,
  expanded,
  onToggle,
  onCancelled,
}: {
  watch: Watch;
  showOwner: boolean;
  expanded: boolean;
  onToggle: () => void;
  onCancelled: () => void;
}) {
  const showToast = useStore((state) => state.showToast);
  const cancel = useCancelWatch();
  const { data: detail, isLoading } = useWatch(expanded ? watch.id : null);

  async function handleCancel(event: MouseEvent<HTMLButtonElement>) {
    event.stopPropagation();
    try {
      await cancel.mutateAsync(watch.id);
      onCancelled();
      showToast("Watch cancelled", "success");
    } catch (error) {
      showToast(error instanceof Error ? error.message : "Failed to cancel watch", "error");
    }
  }

  return (
    <div
      className={clsx("rounded-lg border border-cyber-border", expanded && "border-cyber-cyan/30")}
    >
      <div className="relative transition-colors hover:bg-cyber-surface/20">
        <button
          type="button"
          className="group block w-full cursor-pointer px-4 py-3 pr-16 text-left"
          onClick={onToggle}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="truncate text-sm font-medium text-cyber-text">
                  {getWatchTitle(watch)}
                </span>
                <span
                  className={clsx(
                    "rounded border px-1.5 py-0.5 text-[10px] uppercase",
                    getWatchStatusTone(watch.status),
                  )}
                >
                  {watch.status}
                </span>
                {watch.recurrent && (
                  <span className="rounded border border-cyber-magenta/30 px-1.5 py-0.5 text-[10px] uppercase text-cyber-magenta">
                    Recurring
                  </span>
                )}
              </div>
              <div className="mt-1 text-xs text-cyber-muted">
                {describeWatchCondition(watch.condition)}
              </div>
              <div className="mt-1.5 flex flex-wrap gap-3 text-[11px] text-cyber-muted/70">
                <span>{describeNotifyTarget(watch.notifyTarget)}</span>
                <span>Created {relativeTime(watch.createdAt)}</span>
                {showOwner && <span>{watch.ownerEmail}</span>}
              </div>
            </div>
            <div className="flex items-center gap-1">
              <ChevronDown
                size={16}
                className={clsx("transition-transform", expanded && "rotate-180")}
              />
            </div>
          </div>
        </button>
        {watch.status === "active" && (
          <button
            type="button"
            onClick={handleCancel}
            disabled={cancel.isPending}
            className="absolute right-10 top-3 rounded p-1.5 text-cyber-muted hover:bg-red-400/10 hover:text-red-400 disabled:opacity-50"
            title="Cancel watch"
          >
            <Trash2 size={14} />
          </button>
        )}
      </div>

      {expanded && (
        <div className="space-y-4 border-t border-cyber-border bg-cyber-bg/50 px-4 py-4">
          {isLoading || !detail ? (
            <div className="text-sm text-cyber-muted">Loading watch details...</div>
          ) : (
            <>
              <div className="grid gap-3 md:grid-cols-2">
                <DetailStat label="Source" value={detail.watch.source} />
                <DetailStat label="Owner" value={detail.watch.ownerEmail} />
                <DetailStat label="Created" value={formatAbsoluteTime(detail.watch.createdAt)} />
                <DetailStat label="Expires" value={formatAbsoluteTime(detail.watch.expiresAt)} />
              </div>
              <DetailBlock label="Condition">
                <pre className="overflow-x-auto rounded border border-cyber-border bg-cyber-surface/60 p-3 text-xs text-cyber-muted">
                  {JSON.stringify(detail.watch.condition, null, 2)}
                </pre>
              </DetailBlock>
              <DetailBlock label="Action prompt">
                <pre className="whitespace-pre-wrap rounded border border-cyber-border bg-cyber-surface/60 p-3 text-sm text-cyber-text">
                  {detail.watch.actionPrompt}
                </pre>
              </DetailBlock>
              <DetailBlock label="Trigger history">
                <TriggerList triggers={detail.triggers} recurrent={detail.watch.recurrent} />
              </DetailBlock>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function DetailStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded border border-cyber-border bg-cyber-surface/30 px-3 py-2">
      <div className="text-[10px] uppercase text-cyber-muted">{label}</div>
      <div className="mt-1 text-sm text-cyber-text">{value}</div>
    </div>
  );
}

function DetailBlock({ label, children }: { label: string; children: ReactNode }) {
  return (
    <section>
      <div className="mb-2 text-[10px] uppercase text-cyber-muted">{label}</div>
      {children}
    </section>
  );
}

function TriggerList({ triggers, recurrent }: { triggers: Job[]; recurrent: boolean }) {
  if (triggers.length === 0) {
    return (
      <div className="rounded border border-dashed border-cyber-border px-4 py-5 text-center text-sm text-cyber-muted">
        {recurrent ? "No triggers yet" : "No recorded trigger job"}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {triggers.map((trigger) => (
        <div
          key={trigger.id}
          className="rounded border border-cyber-border bg-cyber-surface/30 p-3"
        >
          <div className="mb-2 flex items-center justify-between gap-3">
            <span className="text-xs text-cyber-muted">{relativeTime(trigger.createdAt)}</span>
            <StatusBadge status={trigger.status} />
          </div>
          {trigger.response ? (
            <div className="prose prose-sm prose-invert max-w-none text-cyber-text">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{trigger.response}</ReactMarkdown>
            </div>
          ) : (
            <p className="text-xs text-cyber-muted">{describeTriggerResult(trigger)}</p>
          )}
        </div>
      ))}
    </div>
  );
}
